import { SyntheticEvent, useState, useMemo } from "react";
import {
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
  ListItem,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CircledBackButton from "@/features/Common/components/CircledBackButton";
import Panel from "@/features/Common/components/Panel";
import theme, { defaultFullscreenPageStyling } from "@/theme";
import { useNavigate } from "react-router-dom";
import { trackEvent, trackPageViewV2 } from "@/services/analytics-adapter";
import { formatDateToReadableString } from "@/utils";
import useGetMemberVouchers from "@/features/Vouchering/hooks/useGetMyVouchers";
import IconLoader from "@/features/Common/components/IconLoader";
import {
  MemberServiceOfferingType,
  VoucherOrderStatus,
} from "@/services/core-api-adapter";
import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";
import DefaultError from "@/features/Common/components/DefaultError";
import WatermarkIcon from "@/features/Common/components/WatermarkIcon";

interface VoucherPanelProps {
  voucherOrderId: string;
  name: string;
  expiryDate: string;
  assignedOnDate: string;
  usedOnDate: string;
  voucherStatus: string;
  voucherCode: string;
  onPanelClick: () => void;
  onActionClick: () => void;
}

function VoucherList({
  vouchers,
  onPanelClick,
  onActionClick,
}: {
  vouchers: any[];
  onPanelClick: (voucher: any) => void;
  onActionClick: (voucher: any) => void;
}) {
  return (
    <>
      {vouchers.map((voucher) => (
        <ListItem
          key={voucher.voucherOrderId}
          role="listitem"
          sx={{ padding: 0 }}
        >
          <VoucherPanel
            voucherOrderId={voucher.voucherOrderId}
            name={voucher.name}
            expiryDate={voucher.expiryDate}
            assignedOnDate={voucher.assignedOn}
            usedOnDate={voucher.usedOn || ""}
            voucherStatus={voucher.voucherStatus}
            voucherCode={voucher.voucherCode}
            onPanelClick={() => onPanelClick(voucher)}
            onActionClick={() => onActionClick(voucher)}
          />
        </ListItem>
      ))}
    </>
  );
}

function VoucherPanel({
  voucherOrderId,
  name,
  expiryDate,
  assignedOnDate,
  usedOnDate,
  voucherStatus,
  voucherCode,
  onPanelClick,
  onActionClick,
}: VoucherPanelProps) {
  const { t } = useTranslation();
  return (
    <Stack
      sx={{
        background: theme.palette.background.paper,
        cursor: "pointer",
        border: "none",
        width: "100%",
      }}
    >
      <Panel sx={{ width: "100%" }}>
        <Stack spacing={2}>
          <Stack
            component="button"
            aria-label={`voucherOrderId-${voucherOrderId}`}
            onClick={onPanelClick}
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            textAlign="left"
            sx={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
            }}
          >
            <Stack spacing={0.5}>
              <Typography variant="body2" fontWeight="bold" color="neutral.500">
                {t("More.MyVouchers.Added.subtitle", {
                  date: formatDateToReadableString(new Date(assignedOnDate)),
                })}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {t("More.MyVouchers.Added.card.title")}
              </Typography>
              <Typography variant="body2">{voucherCode}</Typography>
              <Typography variant="body1" fontWeight="bold">
                {t("More.MyVouchers.Added.card.Services.title")}
              </Typography>
              <Typography variant="body2">{name}</Typography>
            </Stack>
            <IconLoader icon="ChevronNextIcon" />
          </Stack>
          <Stack spacing={0.5} textAlign="left">
            <Typography variant="body2">
              {voucherStatus === VoucherOrderStatus.ASSIGNED &&
                t("More.MyVouchers.Added.card.footerText", {
                  date: formatDateToReadableString(new Date(expiryDate)),
                })}
              {voucherStatus === VoucherOrderStatus.EXPIRED &&
                t("More.MyVouchers.Expired.card.footerText", {
                  date: formatDateToReadableString(new Date(expiryDate)),
                })}
              {voucherStatus === VoucherOrderStatus.REDEEMED &&
                t("More.MyVouchers.Used.card.footerText", {
                  date: formatDateToReadableString(new Date(usedOnDate)),
                })}
            </Typography>
          </Stack>
          {voucherStatus === VoucherOrderStatus.ASSIGNED && (
            <>
              <Stack spacing={1} textAlign="left">
                <Divider />
                <ButtonWithAnalytics
                  page="MyVouchers"
                  text={t("More.MyVouchers.Added.card.buttonText")}
                  intent="navigational"
                  variant="text"
                  onClick={onActionClick}
                  size="small"
                  sx={{ justifyContent: "left", padding: 0 }}
                  endIcon={<IconLoader icon="ChevronNextIcon" />}
                >
                  {t("More.MyVouchers.Added.card.buttonText")}
                </ButtonWithAnalytics>
              </Stack>
            </>
          )}
        </Stack>
      </Panel>
    </Stack>
  );
}

export default function MyVouchers() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const myVoucherRoutes = ["added", "used", "expired"];

  const [selectedTab, setSelectedTab] = useState<string>(myVoucherRoutes[0]);

  const { memberVouchers, isMemberVouchersLoading, memberVouchersFetchError } =
    useGetMemberVouchers();

  function filterVouchersByStatus(voucherStatus: VoucherOrderStatus) {
    return (
      memberVouchers?.vouchers?.filter(
        (x) => x.voucherStatus === voucherStatus
      ) ?? []
    );
  }

  const addedVouchers = useMemo(
    () => filterVouchersByStatus(VoucherOrderStatus.ASSIGNED),
    [memberVouchers]
  );
  const usedVouchers = useMemo(
    () => filterVouchersByStatus(VoucherOrderStatus.REDEEMED),
    [memberVouchers]
  );
  const expiredVouchers = useMemo(
    () => filterVouchersByStatus(VoucherOrderStatus.EXPIRED),
    [memberVouchers]
  );

  let vouchersToShow: any[] = [];
  if (selectedTab === "added") {
    vouchersToShow = addedVouchers;
  } else if (selectedTab === "used") {
    vouchersToShow = usedVouchers;
  } else if (selectedTab === "expired") {
    vouchersToShow = expiredVouchers;
  }

  const noVouchers = !vouchersToShow || vouchersToShow.length === 0;

  function onTabChange(_event: SyntheticEvent, newTab: string) {
    const transformedTabName = newTab
      .split("-")
      .map((word, index) => {
        if (index === 0) {
          return word;
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");

    trackEvent({
      event: `action.myVouchersTabClicked`,
      "myVouchersTabClicked.name": transformedTabName,
      source: "My CareCard vouchers",
    });

    trackPageViewV2({
      pageName: `My CareCard vouchers:${transformedTabName}`,
      pageSubSection1: "My CareCard vouchers",
      pageSubSection2: `My CareCard vouchers:${transformedTabName}`,
      pageCategory: "My CareCard vouchers",
    });

    setSelectedTab(newTab);
  }

  if (isMemberVouchersLoading) {
    return <FullscreenLoadingIndicator />;
  }

  if (memberVouchersFetchError) {
    return (
      <DefaultError>
        <Typography variant="h2">{t("GenericError.title")}</Typography>
        <Typography pb={2}>{t("GenericError.content")}</Typography>
        <Stack direction="row" spacing={2}>
          <Button
            fullWidth
            onClick={() => navigate("/home")}
            variant="contained"
          >
            {t("common.comeBackButton")}
          </Button>
        </Stack>
      </DefaultError>
    );
  }

  function onUseVoucherNowClick(voucher: any) {
    if (voucher.serviceOfferingType === MemberServiceOfferingType.PATHOLOGY) {
      navigate("/home/get-medical-assistance/pathology");
    } else {
      navigate("/home/get-medical-assistance/intercare");
    }
  }

  function onVoucherPanelClick(voucher: any) {
    navigate(
      `/home/get-medical-assistance/vouchers?orderId=${voucher.voucherOrderId}&isMyVouchers=true`
    );
  }

  return (
    <Stack spacing={3} sx={{ ...defaultFullscreenPageStyling }}>
      <CircledBackButton
        showLabel={true}
        onClick={() => navigate("/home/more")}
      />
      <Stack my={3}>
        <Typography variant="h2">{t("More.MyVouchers.title")}</Typography>
      </Stack>
      <Tabs
        value={selectedTab}
        onChange={onTabChange}
        variant="scrollable"
        scrollButtons={false}
        aria-label={t("MyHealth.tabNavigation.label")}
        textColor="primary"
        sx={{
          mx: defaultFullscreenPageStyling.px * -1,
          "& .MuiTabs-flexContainer::after": {
            content: '" "',
            minWidth: (theme) => theme.spacing(defaultFullscreenPageStyling.px),
            position: "relative",
          },
        }}
      >
        <Tab
          value={myVoucherRoutes[0]}
          label={"Added" + " (" + addedVouchers?.length + ")"}
          sx={{
            ml: defaultFullscreenPageStyling.px,
          }}
        />
        <Tab
          value={myVoucherRoutes[1]}
          label={"Used" + " (" + usedVouchers?.length + ")"}
        />
        <Tab
          value={myVoucherRoutes[2]}
          label={"Expired" + " (" + expiredVouchers?.length + ")"}
        />
      </Tabs>
      <Stack
        component="ul"
        role="list"
        aria-label={t("More.MyVouchers.title")}
        spacing={2}
      >
        {noVouchers ? (
          <Stack
            spacing={2}
            alignItems="center"
            justifyContent="center"
            height="80vh"
            width="100%"
          >
            <WatermarkIcon
              height={130}
              width={130}
              color={theme.palette.accent2[100]}
              foreground={theme.palette.info.main}
              Icon={"TrolleyWithPlusIcon"}
            />
            <Typography variant="body1" fontWeight={600} color="neutral.light">
              {t("More.MyVouchers.noVouchers.text", {
                selectedTab,
              })}
            </Typography>
          </Stack>
        ) : (
          <VoucherList
            vouchers={vouchersToShow}
            onPanelClick={onVoucherPanelClick}
            onActionClick={onUseVoucherNowClick}
          />
        )}
      </Stack>
    </Stack>
  );
}
